/**
 * The **turn-virgin window** — one definition shared by Ordnance's firing window and RAW
 * Protocol timing — plus Ordnance's own gates, including the engagement carve-out.
 */

import type { EncounterState } from "@lancer-vtt/domain";
import { activeAttack, turnBudgetVirgin } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { selectWeapon } from "../resolve";
import { isEngagedWith } from "../spatialRecompute";
import type { BudgetVerdict } from "./types";

/**
 * Is `actor`'s turn still **virgin** for a start-of-turn-restricted window — Ordnance's firing
 * window (RAW Core p.105: "can only be fired before the user moves or takes any other actions on
 * their turn, excepting PROTOCOLS") AND RAW Protocol timing (Core p.62: "at the start of their
 * turn, before moving or taking any other action")? The SAME window, one definition.
 *
 * **A THIN WRAPPER.** The window itself is `@lancer-vtt/domain`'s
 * {@link turnBudgetVirgin} — pure over `TurnBudget`, cost-keyed on
 * `TurnBudget.spent` — and that doc carries the rules argument (why cost-keyed, why a spend
 * counter rather than the remaining budget or `actionUses`, and the divergence from the
 * strictest reading of p.105 the developer ruled for). It lives in `domain` because
 * `handlers/turns.ts` and `handlers/placement.ts` consume it too and cannot import from
 * `@lancer-vtt/rules`. All this function adds is the turn-phase/actor short-circuit its
 * `EncounterState`-shaped call sites need, so no call site had to change when the window moved.
 *
 * **Protocols fall out for free** — a Protocol activation costs 0 action points (`pointsForActivation("protocol") === 0`),
 * so it never increments the spend counter, and its own `1/turn` cap rides the protocol-safe
 * `protocolUses` sibling. Since the window is cost-keyed this is no longer a coincidence to be
 * defended: a zero-cost activation of ANY kind leaves the window open by design. The pinned
 * guardian tests named for Ordnance/Protocol in `economy.test.ts` still hold it.
 *
 * Returns `true` when nobody is acting / it isn't `actor`'s turn (the idle inert tier, and a
 * defensive fallback for a caller that got here before `checkBudget`'s own turn-gate would have
 * rejected) — mirrors every other economy check's "no budget ⇒ unconstrained" stance.
 */
export function isTurnVirgin(state: EncounterState, actor: string): boolean {
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== actor) return true;
  return turnBudgetVirgin(turn.budget);
}

/**
 * Ordnance's own name for {@link isTurnVirgin} — a thin, semantically-scoped wrapper so every
 * Ordnance call site/test reads in Ordnance's terms. Protocol timing consumes `isTurnVirgin`
 * directly (see {@link checkGrantedActionLegality}) — the two share one window definition.
 */
export function checkOrdnanceWindow(state: EncounterState, actor: string): boolean {
  return isTurnVirgin(state, actor);
}

/**
 * May `attacker` fire an **Ordnance**-tagged weapon (as {@link selectWeapon} would resolve it)
 * right now? Rejected here, before any dice, the same shape as
 * {@link checkLoaded}/{@link checkWeaponRecharge}. A weapon carrying no Ordnance tag
 * (`weapon.ordnance` absent/false — the common case) is never gated. Rung into `resolveIntent`'s
 * pre-dice ladder for a standard attack (`backend/src/encounterRoom.ts`), a Barrage **opener**,
 * and a Skirmish primary (`midAttack: false`/omitted on all three) — all read the turn LIVE via
 * {@link checkOrdnanceWindow}. A mid-attack-lock **continuation** (`midAttack: true` — a Barrage
 * continuation OR a Skirmish's Aux rider) instead reads the {@link AttackLock.ordnanceWindow}
 * SNAPSHOT captured at `open_attack` — re-deriving live would always read `false` after the
 * opener/primary's own action spent its point(s), wrongly rejecting every continuation shot of an
 * Ordnance-opened attack. `gm_force` is a SEPARATE intent that skips this whole gate
 * (mirroring Loading — never a flag).
 */
export type CheckOrdnanceArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
  /** True for a mid-attack-lock CONTINUATION shot — reads the lock's snapshot instead of the live window. */
  midAttack?: boolean;
};

export function checkOrdnance(args: CheckOrdnanceArgs): BudgetVerdict {
  const { state, attacker, weapon, profile, midAttack } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  const w = selectWeapon(atk, weapon, profile);
  if (!w.ordnance) return ok(undefined);
  const open = midAttack
    ? activeAttack(state.activation)?.ordnanceWindow === true
    : checkOrdnanceWindow(state, attacker);
  if (open) return ok(undefined);
  return err(
    `${w.name} is Ordnance — can only be fired before ${atk.name} moves or takes any other action this turn (excepting Protocols)`,
  );
}

/**
 * May an **Ordnance**-tagged weapon (as {@link selectWeapon} would resolve it) target `target`
 * (RAW Core p.105: "because of its size, this weapon can't be used against targets in engagement
 * with the user's mech")? Rejected here, before any dice. **Pairwise** — reuses
 * {@link isEngagedWith} (`spatialRecompute.ts`, attacker↔target adjacency + hostility),
 * **NEVER** the `Engaged` STATUS (which means "engaged with anyone" — a target across the map
 * would be wrongly rejected just because a THIRD unit happens to be adjacent to the attacker
 * elsewhere). Positions, not status. A weapon carrying no Ordnance tag is never gated.
 */
export type CheckOrdnanceEngagementArgs = {
  state: EncounterState;
  attacker: string;
  target: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkOrdnanceEngagement(args: CheckOrdnanceEngagementArgs): BudgetVerdict {
  const { state, attacker, target, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  const w = selectWeapon(atk, weapon, profile);
  if (!w.ordnance) return ok(undefined);
  if (isEngagedWith(state, attacker, target)) {
    const tgtName = state.combatants[target]?.name ?? target;
    return err(
      `${w.name} is Ordnance — can't target ${tgtName}, which is in engagement with ${atk.name} (RAW p.105)`,
    );
  }
  return ok(undefined);
}

